import React, { useMemo, useState, useEffect } from "react";
import { ArrowRight, Ruler, PaintRoller, DoorOpen, House, CaretLeft, CaretRight, Check, Minus, Plus, AppWindow } from "@phosphor-icons/react";
import { Slider } from "@/components/ui/slider";
import GarageVisualizer from "./GarageVisualizer";

const COLORS = [
  { id: "antracit", label: "Antracit", code: "RAL 7016", hex: "#3A3F44" },
  { id: "feher", label: "Fehér", code: "RAL 9002", hex: "#E7EBDA" },
  { id: "fekete", label: "Fekete", code: "RAL 9005", hex: "#1C1C1E" },
  { id: "bordo", label: "Bordó", code: "RAL 3005", hex: "#5C2025" },
  { id: "aranytolgy", label: "Aranytölgy", code: "", hex: "#A87148" },
  { id: "ezust", label: "Ezüst", code: "RAL 9006", hex: "#A0A4A8", minArea: 200, note: "Csak 200 m² felett" },
];

const DOOR_TYPES = [
  { id: "billeno", label: "Billenő kapu" },
  { id: "szekcionalt", label: "Szekcionált kapu" },
  { id: "automata", label: "Automata szekcionált" },
  { id: "kettos", label: "Kétszárnyú nyíló" },
];

const ROOFS = [
  { id: "nyereg", label: "Nyeregtető", mult: 1.12 },
  { id: "pult_oldal", label: "Pulttető (oldalirányú)", mult: 1.06 },
  { id: "pult_hosszanti", label: "Pulttető (hosszanti)", mult: 1.06 },
];

const THICKNESSES = [
  { id: "40", label: "40 mm", mult: 1.0, hint: "Tároló, melléképület" },
  { id: "60", label: "60 mm", mult: 1.06, hint: "Standard garázs" },
  { id: "100", label: "100 mm", mult: 1.14, hint: "Hőszigetelt, fűtött épület" },
];

const PASSAGE_DOOR_PRICE = 45000;
const WINDOW_PRICE = 35000;

function priceEstimate({ width, length, height, roof, panel_thickness, passage_doors, windows }) {
  const area = width * length;
  const base = 95000;
  const heightFactor = 1 + Math.max(0, (height - 2.5)) * 0.05;
  const roofMult = ROOFS.find((r) => r.id === roof)?.mult ?? 1.06;
  const thickMult = THICKNESSES.find((t) => t.id === panel_thickness)?.mult ?? 1.0;
  const extras = (passage_doors || 0) * PASSAGE_DOOR_PRICE + (windows || 0) * WINDOW_PRICE;
  const price = area * base * heightFactor * roofMult * thickMult + extras;
  return Math.round(price / 10000) * 10000;
}

export default function Configurator({ onChange }) {
  const [step, setStep] = useState(1);
  const [width, setWidth] = useState(3);
  const [length, setLength] = useState(5);
  const [height, setHeight] = useState(2.5);
  const [panelThickness, setPanelThickness] = useState("60");
  const [color, setColor] = useState(COLORS[0].id);
  const [trimColor, setTrimColor] = useState(COLORS[1].id); // default: Fehér szegő
  const [doorCounts, setDoorCounts] = useState({ billeno: 1, szekcionalt: 0, automata: 0, kettos: 0 });
  const [roof, setRoof] = useState(ROOFS[0].id);
  const [passageDoors, setPassageDoors] = useState(0);
  const [windows, setWindows] = useState(0);

  const area = width * length;

  // Build a readable door summary like "1× Billenő, 2× Szekcionált"
  const doorBreakdown = DOOR_TYPES
    .filter((d) => (doorCounts[d.id] || 0) > 0)
    .map((d) => `${doorCounts[d.id]}× ${d.label}`)
    .join(", ") || "—";

  // Primary type for legacy fields = first non-zero
  const primaryDoor = DOOR_TYPES.find((d) => (doorCounts[d.id] || 0) > 0)?.id || "billeno";
  const totalDoors = DOOR_TYPES.reduce((sum, d) => sum + (doorCounts[d.id] || 0), 0);

  // If user dimensions drop below silver threshold while silver is selected, revert to default
  useEffect(() => {
    const cObj = COLORS.find((c) => c.id === color);
    if (cObj?.minArea && area < cObj.minArea) setColor(COLORS[0].id);
    const tObj = COLORS.find((c) => c.id === trimColor);
    if (tObj?.minArea && area < tObj.minArea) setTrimColor(COLORS[1].id);
  }, [area, color, trimColor]);

  const estimated = useMemo(
    () => priceEstimate({ width, length, height, roof, panel_thickness: panelThickness, passage_doors: passageDoors, windows }),
    [width, length, height, roof, panelThickness, passageDoors, windows]
  );

  useEffect(() => {
    onChange?.({
      width, length, height,
      panel_thickness: panelThickness,
      color, trim_color: trimColor,
      door_type: primaryDoor,
      door_breakdown: doorBreakdown,
      door_counts: doorCounts,
      total_doors: totalDoors,
      roof_type: roof,
      passage_doors: passageDoors, windows,
      estimated_price: estimated,
    });
  }, [width, length, height, panelThickness, color, trimColor, doorCounts, primaryDoor, doorBreakdown, totalDoors, roof, passageDoors, windows, estimated, onChange]);

  const currentColor = COLORS.find((c) => c.id === color);
  const currentTrim = COLORS.find((c) => c.id === trimColor);
  const stepLabels = ["Méret", "Szín", "Nyílászárók", "Tető"];

  return (
    <section id="configurator" data-testid="configurator-section" className="py-20 md:py-32 bg-[#FFFFFF] border-y border-[#E4E4E7]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <span className="badge-chip" style={{ background: "#0F0F0F", color: "#FFFFFF" }}>
              Interaktív konfigurátor
            </span>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight mt-4 leading-[0.95]">
              Tervezze meg a saját<br/>garázsát. Most.
            </h2>
            <p className="text-[#525252] mt-4 text-base md:text-lg">
              Méret, szín, nyílászárók, tető — a vázlat valós időben épül fel a választása alapján. A konfiguráció automatikusan betöltődik az ajánlatkérőbe.
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {[1, 2, 3, 4].map((s) => (
              <button
                key={s}
                onClick={() => setStep(s)}
                data-testid={`step-jump-${s}`}
                className="flex items-center gap-2 group"
              >
                <span className={`w-7 h-7 rounded-sm flex items-center justify-center text-xs font-bold transition-all ${
                  s === step
                    ? "bg-[#0F0F0F] text-[#FFFFFF]"
                    : s < step
                    ? "bg-[#DC2626] text-[#FFFFFF]"
                    : "bg-[#FFFFFF] text-[#525252] border border-[#E4E4E7]"
                }`}>
                  {s < step ? <Check size={14} weight="bold" /> : s}
                </span>
                <span className={`text-xs font-semibold uppercase tracking-wider ${s === step ? "text-[#0F0F0F]" : "text-[#525252]"}`}>
                  {stepLabels[s - 1]}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr,1.2fr] gap-0 border border-[#E4E4E7] bg-white">
          {/* Controls */}
          <div className="p-6 md:p-8 bg-[#F5F5F5] border-b lg:border-b-0 lg:border-r border-[#E4E4E7]">
            {step === 1 && (
              <div data-testid="config-step-1">
                <div className="flex items-center gap-2 mb-6">
                  <Ruler size={22} weight="duotone" className="text-[#0F0F0F]" />
                  <h3 className="font-display font-bold text-xl">1. Méretek</h3>
                </div>
                <ControlSlider label="Szélesség" unit="m" value={width} min={2} max={12} step={0.5} onChange={setWidth} testid="slider-width" />
                <ControlSlider label="Hosszúság" unit="m" value={length} min={2} max={20} step={0.5} onChange={setLength} testid="slider-length" />
                <ControlSlider label="Belmagasság" unit="m" value={height} min={2} max={4.5} step={0.05} onChange={setHeight} testid="slider-height" />

                <div className="mt-6 mb-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2">
                    Szendvicspanel vastagság
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {THICKNESSES.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        data-testid={`thickness-${t.id}`}
                        onClick={() => setPanelThickness(t.id)}
                        className={`p-3 border transition-all text-left bg-white ${panelThickness === t.id ? "border-[#0F0F0F] bg-[#F5F5F5]" : "border-[#E4E4E7] hover:border-[#0F0F0F]"}`}
                      >
                        <div className="font-display font-black text-lg leading-none">{t.label}</div>
                        <div className="text-[10px] text-[#525252] mt-1 leading-tight">{t.hint}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4 p-4 bg-white border border-[#E4E4E7]">
                  <div className="text-xs text-[#525252] uppercase tracking-wider mb-1">Alapterület</div>
                  <div className="font-display font-black text-2xl">{area.toFixed(1)} m²</div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="config-step-2">
                <div className="flex items-center gap-2 mb-6">
                  <PaintRoller size={22} weight="duotone" className="text-[#0F0F0F]" />
                  <h3 className="font-display font-bold text-xl">2. Színek</h3>
                </div>

                <div className="mb-6">
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2">
                    Homlokzat (falpanel) szín
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {COLORS.map((c) => {
                      const locked = !!(c.minArea && area < c.minArea);
                      return (
                        <button
                          key={c.id}
                          data-testid={`color-${c.id}`}
                          onClick={() => !locked && setColor(c.id)}
                          disabled={locked}
                          title={locked ? c.note : ""}
                          className={`text-left p-2 border transition-all bg-white relative ${color === c.id ? "border-[#0F0F0F]" : "border-[#E4E4E7] hover:border-[#0F0F0F]"} ${locked ? "opacity-50 cursor-not-allowed hover:border-[#E4E4E7]" : ""}`}
                        >
                          <div className={`swatch ${color === c.id ? "selected" : ""}`} style={{ background: c.hex }} />
                          <div className="mt-1.5">
                            <div className="text-xs font-bold flex items-center gap-1">
                              {c.label}
                              {locked && <span className="text-[9px] font-bold text-[#DC2626]">🔒</span>}
                            </div>
                            <div className="text-[10px] text-[#525252]">{c.code}</div>
                            {c.note && (
                              <div className="text-[9px] text-[#DC2626] font-semibold mt-0.5">{c.note}</div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2">
                    Szegők (élek, tetőszegély) szín
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {COLORS.map((c) => {
                      const locked = !!(c.minArea && area < c.minArea);
                      return (
                        <button
                          key={c.id}
                          data-testid={`trim-${c.id}`}
                          onClick={() => !locked && setTrimColor(c.id)}
                          disabled={locked}
                          title={locked ? c.note : ""}
                          className={`text-left p-2 border transition-all bg-white relative ${trimColor === c.id ? "border-[#0F0F0F]" : "border-[#E4E4E7] hover:border-[#0F0F0F]"} ${locked ? "opacity-50 cursor-not-allowed hover:border-[#E4E4E7]" : ""}`}
                        >
                          <div className={`swatch ${trimColor === c.id ? "selected" : ""}`} style={{ background: c.hex }} />
                          <div className="mt-1.5">
                            <div className="text-xs font-bold flex items-center gap-1">
                              {c.label}
                              {locked && <span className="text-[9px] font-bold text-[#DC2626]">🔒</span>}
                            </div>
                            <div className="text-[10px] text-[#525252]">{c.code}</div>
                            {c.note && (
                              <div className="text-[9px] text-[#DC2626] font-semibold mt-0.5">{c.note}</div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="config-step-3">
                <div className="flex items-center gap-2 mb-6">
                  <DoorOpen size={22} weight="duotone" className="text-[#0F0F0F]" />
                  <h3 className="font-display font-bold text-xl">3. Nyílászárók</h3>
                </div>

                <div className="mb-6">
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2">
                    Garázskapu típus és darabszám
                  </div>
                  <div className="flex flex-col gap-2">
                    {DOOR_TYPES.map((d) => {
                      const cnt = doorCounts[d.id] || 0;
                      const active = cnt > 0;
                      return (
                        <div
                          key={d.id}
                          data-testid={`door-row-${d.id}`}
                          className={`flex items-center justify-between gap-3 p-3 border transition-all bg-white ${active ? "border-[#0F0F0F] bg-[#F5F5F5]" : "border-[#E4E4E7]"}`}
                        >
                          <span className="text-sm font-semibold">{d.label}</span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              data-testid={`door-${d.id}-minus`}
                              onClick={() => setDoorCounts({ ...doorCounts, [d.id]: Math.max(0, cnt - 1) })}
                              disabled={cnt <= 0}
                              className="w-8 h-8 border border-[#0F0F0F] flex items-center justify-center hover:bg-[#0F0F0F] hover:text-[#FFFFFF] disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#0F0F0F] transition-colors"
                            >
                              <Minus size={14} weight="bold" />
                            </button>
                            <span data-testid={`door-${d.id}-value`} className="w-7 text-center font-display font-black text-lg">{cnt}</span>
                            <button
                              type="button"
                              data-testid={`door-${d.id}-plus`}
                              onClick={() => setDoorCounts({ ...doorCounts, [d.id]: Math.min(3, cnt + 1) })}
                              disabled={cnt >= 3}
                              className="w-8 h-8 border border-[#0F0F0F] flex items-center justify-center hover:bg-[#0F0F0F] hover:text-[#FFFFFF] disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#0F0F0F] transition-colors"
                            >
                              <Plus size={14} weight="bold" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Counter
                    testid="passage-doors"
                    label="Bejárati ajtó"
                    hint="90 × 200 cm"
                    value={passageDoors}
                    onChange={setPassageDoors}
                    min={0}
                    max={3}
                  />
                  <Counter
                    testid="windows"
                    label="Ablak"
                    hint="90 × 90 cm"
                    value={windows}
                    onChange={setWindows}
                    min={0}
                    max={6}
                    icon={AppWindow}
                  />
                </div>

                <div className="mt-4 p-3 bg-white border border-[#E4E4E7] text-xs text-[#525252] leading-relaxed">
                  Eltérő méret? Írja be az <strong>Egyéb igény</strong> mezőbe az ajánlatkérőnél.
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="config-step-4">
                <div className="flex items-center gap-2 mb-6">
                  <House size={22} weight="duotone" className="text-[#0F0F0F]" />
                  <h3 className="font-display font-bold text-xl">4. Tetőforma</h3>
                </div>
                <div className="flex flex-col gap-2">
                  {ROOFS.map((r) => (
                    <button
                      key={r.id}
                      data-testid={`roof-${r.id}`}
                      onClick={() => setRoof(r.id)}
                      className={`flex items-center justify-between p-3 border transition-all text-left bg-white ${roof === r.id ? "border-[#0F0F0F] bg-[#F5F5F5]" : "border-[#E4E4E7] hover:border-[#0F0F0F]"}`}
                    >
                      <span className="text-sm font-semibold">{r.label}</span>
                      {roof === r.id && <Check size={16} weight="bold" className="text-[#0F0F0F]" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#E4E4E7]">
              <button
                data-testid="config-prev"
                onClick={() => setStep(Math.max(1, step - 1))}
                disabled={step === 1}
                className="flex items-center gap-2 text-sm font-semibold text-[#525252] disabled:opacity-30 hover:text-[#0F0F0F]"
              >
                <CaretLeft size={16} weight="bold" /> Vissza
              </button>
              {step < 4 ? (
                <button data-testid="config-next" onClick={() => setStep(step + 1)} className="cta-primary text-sm">
                  Következő <CaretRight size={16} weight="bold" />
                </button>
              ) : (
                <a href="#quote" data-testid="config-finish" className="cta-accent text-sm">
                  Ajánlatot kérek erre <ArrowRight size={16} weight="bold" />
                </a>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="relative bg-white">
            <div className="relative h-72 md:h-96 lg:h-[480px] overflow-hidden bg-[#FFFFFF] border-b border-[#E4E4E7]">
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between gap-3 flex-wrap z-10">
                <span className="badge-chip" style={{ background: "#FFFFFF", color: "#0F0F0F", border: "1px solid #E4E4E7" }}>
                  Élő vázlat · {step}/4 lépés
                </span>
                {step >= 2 && (
                  <div className="flex items-center gap-2">
                    <span className="badge-chip" style={{ background: currentColor?.hex, color: currentColor?.id === "feher" ? "#0F0F0F" : "#FFFFFF" }}>
                      Panel: {currentColor?.label}
                    </span>
                    <span className="badge-chip" style={{ background: currentTrim?.hex, color: currentTrim?.id === "feher" ? "#0F0F0F" : "#FFFFFF" }}>
                      Szegő: {currentTrim?.label}
                    </span>
                  </div>
                )}
              </div>
              <GarageVisualizer
                width={width}
                length={length}
                height={height}
                color={currentColor?.hex || "#3A3F44"}
                trimColor={currentTrim?.hex || "#0F0F0F"}
                door={primaryDoor}
                roof={roof.startsWith("pult") ? "pult" : roof}
                step={step}
              />
            </div>

            <div className="p-6 md:p-8 bg-[#0F0F0F] text-[#FFFFFF]">
              <div className="text-xs uppercase tracking-wider text-[#FFFFFF] mb-2">
                Becsült nettó ár (tájékoztató)
              </div>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="number-huge text-4xl md:text-5xl text-[#FFFFFF]" data-testid="config-price">
                  {estimated.toLocaleString("hu-HU")}
                </span>
                <span className="text-lg font-semibold text-[#E4E4E7]">Ft</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <InfoRow l="Méret" v={`${width} × ${length} × ${height} m`} />
                <InfoRow l="Alapterület" v={`${(width * length).toFixed(1)} m²`} />
                <InfoRow l="Panel vastagság" v={`${panelThickness} mm`} />
                <InfoRow l="Panel / Szegő" v={`${currentColor?.label} / ${currentTrim?.label}`} />
                <InfoRow l={`Kapu (${totalDoors} db)`} v={doorBreakdown} />
                <InfoRow l="Tető" v={ROOFS.find((r) => r.id === roof)?.label} />
                <InfoRow l="Ajtó / Ablak" v={`${passageDoors} / ${windows} db`} />
              </div>
              <div className="mt-6 text-xs text-[#E4E4E7]/70">
                * A pontos ár a helyszínfelmérés után véglegesíthető.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ControlSlider({ label, unit, value, min, max, step, onChange, testid }) {
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-semibold text-[#0F0F0F]">{label}</label>
        <span className="font-display font-black text-lg" data-testid={`${testid}-value`}>
          {value} <span className="text-sm text-[#525252]">{unit}</span>
        </span>
      </div>
      <Slider
        data-testid={testid}
        value={[value]}
        onValueChange={(v) => onChange(v[0])}
        min={min}
        max={max}
        step={step}
      />
      <div className="flex justify-between text-xs text-[#525252]/70 mt-1">
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
}

function Counter({ testid, label, hint, value, onChange, min = 0, max = 10 }) {
  return (
    <div className="p-3 bg-white border border-[#E4E4E7]">
      <div className="text-sm font-bold text-[#0F0F0F]">{label}</div>
      <div className="text-[10px] text-[#525252] uppercase tracking-wider mb-2">{hint}</div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid={`${testid}-minus`}
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          className="w-8 h-8 border border-[#0F0F0F] flex items-center justify-center hover:bg-[#0F0F0F] hover:text-[#FFFFFF] disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#0F0F0F] transition-colors"
        >
          <Minus size={14} weight="bold" />
        </button>
        <span data-testid={`${testid}-value`} className="flex-1 text-center font-display font-black text-xl">
          {value}
        </span>
        <button
          type="button"
          data-testid={`${testid}-plus`}
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          className="w-8 h-8 border border-[#0F0F0F] flex items-center justify-center hover:bg-[#0F0F0F] hover:text-[#FFFFFF] disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#0F0F0F] transition-colors"
        >
          <Plus size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function InfoRow({ l, v }) {
  return (
    <div className="flex flex-col border-l-2 border-[#FFFFFF] pl-2">
      <span className="text-[#FFFFFF] uppercase tracking-wider text-[10px]">{l}</span>
      <span className="font-medium text-[#FFFFFF] text-xs">{v}</span>
    </div>
  );
}
